import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { isActivePatronRequestSeries } from "@/features/BookingParts/components/BookingInfo/isActivePatronRequestSeries";
import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner";
import { InfoIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { RequestInfoBanner_booking$key } from "./__generated__/RequestInfoBanner_booking.graphql";

type Props = {
  bookingRef: RequestInfoBanner_booking$key | null;
  isRecurringSeries?: boolean;
};

export const RequestInfoBanner = ({
  bookingRef,
  isRecurringSeries = false,
}: Props) => {
  const data = useFragment(RequestInfoBannerFragment, bookingRef);

  if (data?.startSlot?.__typename !== "QueueSlot") {
    return null;
  }

  const hasPersistedSeries = isActivePatronRequestSeries({
    hasSeriesRecurrence: !!data.bookingSeries?.recurrenceType,
    remainingBookingCount:
      data.__typename === "StandardBooking" ||
      data.__typename === "LessonBooking"
        ? data.seriesCount ?? 0
        : 0,
  });
  const showRecurringCopy = isRecurringSeries || hasPersistedSeries;

  const message = showRecurringCopy
    ? t`These are requests, not confirmed bookings. Each one is handled on its own date. If a time becomes available, you will receive a notification of your confirmed time for that date.`
    : t`This is a request, not a confirmed booking. You will receive a notification of your confirmed time when/if a time has been assigned.`;

  return (
    <Box marginTop={2}>
      <InfoBanner
        leadingIcon={InfoIcon}
        message={message}
        colorTheme="BLUE"
        testID="request-info-banner"
      />
    </Box>
  );
};

export const RequestInfoBannerFragment = graphql`
  fragment RequestInfoBanner_booking on Booking {
    __typename
    ... on StandardBooking {
      seriesCount
      request {
        time
      }
    }
    ... on LessonBooking {
      seriesCount
      request {
        time
      }
    }
    bookingSeries {
      recurrenceType
    }
    startSlot {
      __typename
      ... on QueueSlot {
        agenda {
          date
        }
      }
    }
  }
`;
